import { useEffect, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { config } from '@/config';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import { getUploadById } from '@/features/uploads/data';
import { useNotifications } from '@/providers/notificationHooks';
import { apiClient } from '@/shared/data/client';
import {
  createNotificationsStream,
  normalizeScope,
  type NotificationPayload,
  type NotificationScope,
} from '@/shared/notifications/notificationsStream';
import { consumeUploadToastIntent } from '@/shared/notifications/uploadToastIntent';
import { Inline, Link, Text } from '@/ui';
import { useAccessToken } from './auth';
import {
  gridPaths,
  invalidateBuilds,
  invalidateExecutionRuns,
  invalidateExecutionRunSummaries,
  invalidateFeatures,
  invalidateUploads,
  invalidateInviteNotifications,
  invalidateMetrics,
  invalidateOrganisations,
  invalidateProjects,
  invalidateReleases,
  invalidateResultsFiles,
  invalidateSuiteExecutions,
  invalidateSuites,
  invalidateTestExecutions,
  invalidateTests,
  invalidateUsers,
  invalidateGridQuery,
} from './queryKeys';
import { useInvalidateQueries } from './useInvalidateQueries';

type Invalidation = ReturnType<typeof invalidateProjects>;
type InvalidationContext = { orgId: string | null; projectId: string | null };

const buildOrgInvalidations = ({ orgId }: InvalidationContext): Invalidation[] => [
  invalidateOrganisations(),
  invalidateUsers(orgId),
  invalidateProjects(orgId),
  invalidateGridQuery(gridPaths.organisations),
  invalidateGridQuery(gridPaths.users),
  invalidateGridQuery(gridPaths.projects),
];

const buildProjectInvalidations = ({ orgId, projectId }: InvalidationContext): Invalidation[] => [
  invalidateProjects(orgId),
  invalidateFeatures(projectId, orgId),
  invalidateReleases(projectId, orgId),
  invalidateSuites(projectId, orgId),
  invalidateSuiteExecutions(projectId, orgId),
  invalidateTests(projectId, orgId),
  invalidateTestExecutions(projectId, orgId),
  invalidateBuilds(projectId, orgId),
  invalidateMetrics(orgId, projectId),
  invalidateGridQuery(gridPaths.projects),
  invalidateGridQuery(gridPaths.features),
  invalidateGridQuery(gridPaths.releases),
  invalidateGridQuery(gridPaths.suites),
  invalidateGridQuery(gridPaths.tests),
  invalidateGridQuery(gridPaths.suiteExecutions),
  invalidateGridQuery(gridPaths.testExecutions),
  invalidateGridQuery(gridPaths.builds),
];

const buildUploadInvalidations = ({ orgId, projectId }: InvalidationContext): Invalidation[] => [
  invalidateUploads(orgId, projectId),
  invalidateResultsFiles(orgId, projectId),
  invalidateExecutionRuns(orgId, projectId),
  invalidateExecutionRunSummaries(),
  invalidateSuiteExecutions(projectId, orgId),
  invalidateTestExecutions(projectId, orgId),
  invalidateSuites(projectId, orgId),
  invalidateTests(projectId, orgId),
  invalidateBuilds(projectId, orgId),
  invalidateMetrics(orgId, projectId),
  invalidateGridQuery(gridPaths.uploads),
  invalidateGridQuery(gridPaths.resultsFiles),
  invalidateGridQuery(gridPaths.executionRuns),
];

const scopeInvalidationBuilders: Record<
  Exclude<NotificationScope, 'all'>,
  (context: InvalidationContext) => Invalidation[]
> = {
  org: buildOrgInvalidations,
  project: buildProjectInvalidations,
  feature: buildProjectInvalidations,
  release: buildProjectInvalidations,
  suite: buildProjectInvalidations,
  test: buildProjectInvalidations,
  execution: buildProjectInvalidations,
  build: buildProjectInvalidations,
  upload: buildUploadInvalidations,
  results: buildUploadInvalidations,
};

export const useNotificationsStream = (): void => {
  const token = useAccessToken();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const invalidateQueries = useInvalidateQueries();
  const { notify } = useNotifications();
  const pendingScopes = useRef<Set<NotificationScope>>(new Set());
  const flushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const uploadToastState = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    if (!token) return;
    let stream: { close: () => void } | null = null;
    let isActive = true;

    const buildInvalidations = (
      scopes: Set<NotificationScope>,
    ): Array<ReturnType<typeof invalidateProjects>> => {
      const invalidations = new Map<string, ReturnType<typeof invalidateProjects>>();
      const addInvalidations = (items: Array<ReturnType<typeof invalidateProjects>>): void => {
        for (const item of items) {
          const key = JSON.stringify('queryKey' in item ? item.queryKey : item);
          if (!invalidations.has(key)) {
            invalidations.set(key, item);
          }
        }
      };
      const context: InvalidationContext = {
        orgId: selectedOrganisationId ?? null,
        projectId: selectedProjectId ?? null,
      };
      const hasAll = scopes.has('all');
      if (hasAll) {
        (
          Object.keys(scopeInvalidationBuilders) as Array<Exclude<NotificationScope, 'all'>>
        ).forEach((scope) => {
          addInvalidations(scopeInvalidationBuilders[scope](context));
        });
      } else {
        scopes.forEach((scope) => {
          if (scope === 'all') return;
          addInvalidations(scopeInvalidationBuilders[scope](context));
        });
      }

      return Array.from(invalidations.values());
    };

    const flushInvalidations = (): void => {
      flushTimer.current = null;
      if (pendingScopes.current.size === 0) return;
      const scopes = new Set(pendingScopes.current);
      pendingScopes.current.clear();
      void invalidateQueries(buildInvalidations(scopes));
    };

    const queueInvalidation = (scope: NotificationScope): void => {
      pendingScopes.current.add(scope);
      if (flushTimer.current) return;
      flushTimer.current = setTimeout(flushInvalidations, 750);
    };

    const handleInvite = (): void => {
      void invalidateQueries([invalidateInviteNotifications()]);
    };

    const handleDataChanged = (payload: NotificationPayload): void => {
      if (payload.projectId && selectedProjectId && payload.projectId !== selectedProjectId) {
        return;
      }
      if (payload.orgId && selectedOrganisationId && payload.orgId !== selectedOrganisationId) {
        return;
      }
      queueInvalidation(normalizeScope(payload.scope));
    };

    const handleUpload = async (payload: NotificationPayload): Promise<void> => {
      if (payload.projectId && selectedProjectId && payload.projectId !== selectedProjectId) {
        return;
      }
      if (payload.orgId && selectedOrganisationId && payload.orgId !== selectedOrganisationId) {
        return;
      }
      queueInvalidation('upload');

      if (!payload.uploadId || !payload.status) return;
      if (!['completed', 'completed_with_errors', 'failed'].includes(payload.status)) {
        return;
      }
      // Only show upload completion/failure toasts for uploads initiated by this web client.
      if (!consumeUploadToastIntent(payload.uploadId)) {
        return;
      }
      const lastStatus = uploadToastState.current.get(payload.uploadId);
      if (lastStatus === payload.status) return;
      uploadToastState.current.set(payload.uploadId, payload.status);

      const fileLabel = payload.fileName ?? 'results';
      const errorSuffix = payload.error ? ` — ${payload.error}` : '';
      if (payload.status === 'completed') {
        const upload = await getUploadById(payload.uploadId, selectedOrganisationId ?? undefined);
        const resultsFileSlug = upload.resultsFileSlug;
        const href = resultsFileSlug
          ? `/results-files/${resultsFileSlug}`
          : `/uploads/${payload.uploadId}`;
        notify({
          message: (
            <Inline gap="sm" align="center">
              <Text role="body" component="span">
                {fileLabel} uploaded
              </Text>
              <Link component={RouterLink} to={href} underline="always" color="inherit">
                View
              </Link>
            </Inline>
          ),
          tone: 'green',
        });
        return;
      }
      const message =
        payload.status === 'completed_with_errors'
          ? `Upload completed with errors: ${fileLabel}${errorSuffix}.`
          : `Upload failed: ${fileLabel}${errorSuffix}.`;
      const tone = payload.status === 'completed_with_errors' ? 'orange' : 'red';
      const failureHref = `/uploads/${payload.uploadId}`;
      notify({
        message: (
          <Inline gap="sm" align="center">
            <Text role="body" component="span">
              {message}
            </Text>
            <Link component={RouterLink} to={failureHref} underline="always" color="inherit">
              View
            </Link>
          </Inline>
        ),
        tone,
      });
    };

    const startStream = async (): Promise<void> => {
      try {
        stream = await createNotificationsStream({
          apiBaseUrl: config.apiBaseUrl,
          orgId: selectedOrganisationId ?? null,
          getStreamToken: async (orgId) => {
            const body = orgId ? { orgId } : undefined;
            const response = await apiClient.post<{ token: string }>(
              'notifications/stream-token',
              body,
              { orgId: orgId ?? undefined },
            );
            return response.token;
          },
          handlers: {
            onInvite: handleInvite,
            onDataChanged: handleDataChanged,
            onUpload: (payload) => {
              void handleUpload(payload);
            },
          },
        });
        if (!isActive) {
          stream.close();
          stream = null;
        }
      } catch {
        // ignore failures; stream will retry on next effect run
      }
    };

    let connectTimer: ReturnType<typeof setTimeout> | null = null;
    const connectDelayMs = 1_200;
    connectTimer = setTimeout(() => {
      void startStream();
    }, connectDelayMs);

    return () => {
      isActive = false;
      if (connectTimer) {
        clearTimeout(connectTimer);
        connectTimer = null;
      }
      if (flushTimer.current) {
        clearTimeout(flushTimer.current);
        flushTimer.current = null;
      }
      if (stream) {
        stream.close();
        stream = null;
      }
    };
  }, [invalidateQueries, selectedOrganisationId, selectedProjectId, token]);
};
